import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import authService from '../services/authService';

// Define logo URLs with paths to public assets folder
const LOGO_URL = "/assets/Hyundai_Logo_Stacked_CMYK.jpg";
const SUBTITLE_LOGO_URL = "/assets/HPP_2016_LOGO_PRIMARY.jpg";
const BACKGROUND_IMAGE_URL = "/assets/hyundai-background.jpg";

const LoginPage = ({ error: propError }) => {
  const [error, setError] = useState(propError || null);
  const [redirectToHome, setRedirectToHome] = useState(false);

  useEffect(() => {
    console.log("LoginPage mounted");
    
    // Set error from props if provided
    if (propError) {
      setError(propError);
    }
    
    // Check if user is already authenticated
    if (authService.isAuthenticated()) {
      console.log("User already authenticated, redirecting to dashboard");
      setRedirectToHome(true);
    }
    
    return () => {
      console.log("LoginPage unmounted");
    };
  }, [propError]);

  const handleLogin = () => {
    try {
      console.log("Initiating login process");
      const result = authService.login();
      if (result && result.error) {
        console.error("Login error:", result.error);
        setError(result.error);
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Authentication failed. Please try again.');
    }
  };

  if (redirectToHome) {
    console.log("Redirecting to dashboard");
    return <Navigate to="/" replace />;
  }

  return (
    <LoginContainer>
      <LoginBox>
        <LoginHeader>
          <LogoStack>
            <HyundaiLogo src={LOGO_URL} alt="Hyundai Logo" />
            <SubtitleLogo src={SUBTITLE_LOGO_URL} alt="Subtitle Logo" />
          </LogoStack>
          <LoginTitle>Hyundai Call Recording Portal</LoginTitle>
        </LoginHeader>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <LoginOptions>
          <LoginButton onClick={handleLogin}>
            <i className="fas fa-sign-in-alt"></i>
            Login
          </LoginButton>
        </LoginOptions>
      </LoginBox>
    </LoginContainer>
  );
};

// Styled Components
const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 20px;
  position: relative;
  background: url('${BACKGROUND_IMAGE_URL}') no-repeat center center;
  background-size: cover;
  
  /* Removing white overlay to show background image clearly */
  /*
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    z-index: 0;
  }
  */
`;

const LoginBox = styled.div`
  background: var(--glass-bg);
  padding: 40px;
  border-radius: 16px;
  box-shadow: var(--shadow);
  width: 100%;
  max-width: 400px;
  text-align: center;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(10px);
  border: 1px solid var(--glass-border);
  transform: translateY(0);
  transition: var(--transition);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  }
`;

const LoginHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const LogoStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
`;

const HyundaiLogo = styled.img`
  width: 100px;
  height: auto;
`;

const SubtitleLogo = styled.img`
  width: 100px;
  height: auto;
`;

const LoginTitle = styled.h1`
  color: var(--primary-color);
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const LoginOptions = styled.div`
  margin-top: 20px;
`;

const LoginButton = styled.button`
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  transition: var(--transition);
  
  &:hover {
    background: var(--secondary-color);
    transform: translateY(-2px);
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  background-color: rgba(231, 76, 60, 0.1);
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  animation: fadeIn 0.3s ease;
`;

export default LoginPage; 